import React from "react";
import { StoreSearchFilters } from "tsi-common-react/src/apps/retail/models";
import { FORM_FIELD_CLASS_NAME } from "tsi-common-react/src/forms/classNames";
import { Meters } from "tsi-common-react/src/models/nominals";
import {
    DISTANCE_OPTIONS,
    DistanceUnit,
    displayUnitToMeters,
    getDistanceUnit,
    metersToDisplayUnit,
} from "tsi-common-react/src/utils/distance";
import { c, t } from "ttag";

import { interpolate } from "@thelabnyc/thelabui/src/utils/i18n";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { LocatorFilterPopover } from "../LocatorFilters_Popover";
import { FilterChangeHandler } from "../models";

const DISTANCE_DIALOG_TITLE_ID = "distance-filter-dialog-title";

export const DistanceFilter = (props: {
    filterValues: StoreSearchFilters;
    onChange: FilterChangeHandler;
}): React.ReactNode => {
    const [value, setValue] = React.useState<Meters>(
        props.filterValues.distance,
    );
    const filterElemRef = React.useRef<{
        onRequestClose: () => void;
    }>(null);

    const unit = getDistanceUnit();
    const distanceOptions = DISTANCE_OPTIONS[unit];

    // Sync local state with Redux state when it changes
    React.useEffect(() => {
        setValue(props.filterValues.distance);
    }, [props.filterValues.distance]);

    const handleChange = (event: React.FormEvent<HTMLSelectElement>) => {
        const displayValue = parseInt(event.currentTarget.value, 10);
        setValue(displayUnitToMeters(displayValue));
    };

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        props.onChange({
            distance: value,
        });
        if (filterElemRef.current) {
            filterElemRef.current.onRequestClose();
        }
    };

    const handleAfterClose = () => {
        // Revert filter back to the state in Redux
        setValue(props.filterValues.distance);
    };

    const labelPattern =
        unit === DistanceUnit.KILOMETERS
            ? c("DistanceFilter").t`%s km`
            : c("DistanceFilter").t`%s miles`;

    // Snap a raw display value to the nearest dropdown option
    const snapToOption = (raw: number): number =>
        distanceOptions.reduce((prev, curr) =>
            Math.abs(curr - raw) < Math.abs(prev - raw) ? curr : prev,
        );

    // Convert meters to display unit for label and select value,
    // snapped to the nearest dropdown option so they stay in sync.
    const displayValue = snapToOption(Math.round(metersToDisplayUnit(value)));
    const displayValueForLabel = snapToOption(
        Math.round(metersToDisplayUnit(props.filterValues.distance)),
    );

    return (
        <LocatorFilterPopover
            ref={filterElemRef}
            id="distance"
            labelText={interpolate(labelPattern, [displayValueForLabel])}
            dialogLabelledById={DISTANCE_DIALOG_TITLE_ID}
            onAfterClose={handleAfterClose}
        >
            <form
                className="filters__dropdown filters__dropdown--distance"
                onSubmit={handleSubmit}
            >
                <fieldset>
                    <legend
                        className="filters__instruct"
                        id={DISTANCE_DIALOG_TITLE_ID}
                    >
                        {t`Change the distance search radius.`}
                    </legend>
                    <div
                        className={concatClassNames([
                            FORM_FIELD_CLASS_NAME,
                            "filters__inputs",
                        ])}
                    >
                        <label htmlFor="bh-sl-maxdistance"></label>
                        <select
                            id="bh-sl-maxdistance"
                            className="form__select"
                            name="bh-sl-maxdistance"
                            aria-label={t`Max Distance`}
                            onChange={handleChange}
                            value={displayValue}
                        >
                            {distanceOptions.map((opt) => {
                                return (
                                    <option key={opt} value={opt}>
                                        {interpolate(labelPattern, [opt])}
                                    </option>
                                );
                            })}
                        </select>
                    </div>
                    <div className="filters__actions sl-search__actions">
                        <input
                            className="filters__button button"
                            type="submit"
                            aria-label={t`Submit`}
                        />
                    </div>
                </fieldset>
            </form>
        </LocatorFilterPopover>
    );
};
